// Generic motion wrapper for content segments on the Home scroll timeline.

import React from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';
import {
  sampleStops,
  toLocalClipVh,
} from '../../../utilities/scrollTimelineMath';
import type { ScrollContentSegment } from '../../../types/homeScroll';
import type { MotionStop } from '../../../types/homeScroll';

const DEFAULT_OPACITY_TRACK: MotionStop[] = [[0, 1]];
const DEFAULT_TRANSLATE_TRACK: MotionStop[] = [[0, 0]];
const DEFAULT_SCALE_TRACK: MotionStop[] = [[0, 1]];
const DEFAULT_ROTATE_TRACK: MotionStop[] = [[0, 0]];

const joinClassNames = (...classNames: Array<string | undefined>): string =>
  classNames.filter(Boolean).join(' ');

type ScrollContentProps = {
  scrollVh: MotionValue<number>;
  /** Optional inline style merged into the segment wrapper (e.g. for runtime
   *  measurements that override Tailwind positioning classes). */
  style?: React.CSSProperties;
} & Omit<ScrollContentSegment, 'id'>;

const ScrollContent: React.FC<ScrollContentProps> = ({
  scrollVh,
  startVh,
  durationVh = 100,
  children,
  shellClassName,
  tracks,
  zIndex = 4,
  style,
}) => {
  const toLocalVh = (value: number): number =>
    toLocalClipVh(value, startVh, durationVh);

  const opacity = useTransform(scrollVh, (value) =>
    sampleStops(tracks?.opacity ?? DEFAULT_OPACITY_TRACK, toLocalVh(value))
  );
  const translateX = useTransform(scrollVh, (value) =>
    sampleStops(tracks?.xPx ?? DEFAULT_TRANSLATE_TRACK, toLocalVh(value))
  );
  const translateY = useTransform(scrollVh, (value) =>
    sampleStops(tracks?.yPx ?? DEFAULT_TRANSLATE_TRACK, toLocalVh(value))
  );
  const scale = useTransform(scrollVh, (value) =>
    sampleStops(tracks?.scale ?? DEFAULT_SCALE_TRACK, toLocalVh(value))
  );
  const rotate = useTransform(scrollVh, (value) =>
    sampleStops(tracks?.rotateDeg ?? DEFAULT_ROTATE_TRACK, toLocalVh(value))
  );

  // Transparent OR out-of-range segments should not block pointer events
  // behind them. Out-of-range matters because a segment without an opacity
  // track (e.g. one whose children handle their own visibility) would
  // otherwise keep its full-rect wrapper clickable across the whole page,
  // trapping clicks on segments stacked below it.
  const pointerEvents = useTransform(scrollVh, (value) => {
    if (value < startVh || value > startVh + durationVh) return 'none';
    const sampledOpacity = sampleStops(
      tracks?.opacity ?? DEFAULT_OPACITY_TRACK,
      toLocalVh(value)
    );
    return sampledOpacity <= 0.01 ? 'none' : 'auto';
  });

  return (
    <motion.div
      className={joinClassNames('absolute transform-gpu', shellClassName)}
      style={{
        opacity,
        pointerEvents,
        x: translateX,
        y: translateY,
        scale,
        rotate,
        zIndex,
        ...style,
      }}
    >
      {children}
    </motion.div>
  );
};

export default ScrollContent;
